import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ID_PROOFING_MAX, voiNahda } from "../utils/oikeudet";
import styled, { useTheme } from "styled-components";
import { Button, Heading, InlineAlert } from "suomifi-ui-components";
import {
  IconArrowLeft,
  IconCheckCircle,
  IconLock,
  IconRemove,
} from "suomifi-icons";
import { useAppState } from "../state/store";
import { normalizeTimestamp, formatFinnishDateShort } from "../utils/dateUtils";
import { naytaNimi, onTunnistautumaton } from "../utils/kayttajaNimi";
import HenkilollisyysBadge from "../components/HenkilollisyysBadge";
import TilaBadge from "../components/TilaBadge";
import Dokumentit from "../components/Dokumentit";
import Laitteet from "../components/Laitteet";
import Tapahtumat from "../components/Tapahtumat";
import HenkilollisyydenTodentaminen from "../components/HenkilollisyydenTodentaminen";
import AuditTrail from "../components/AuditTrail";
import MuistiinpanoSticky from "../components/MuistiinpanoSticky";
import KayttajaSidebar from "../components/KayttajaSidebar";
import KortinHyppylinkit, {
  type HyppylinkkiKohde,
} from "../components/KortinHyppylinkit";
import SektioHaitari from "../components/SektioHaitari";
import PerusteModal from "../components/PerusteModal";
import TuhoavaButton from "../components/TuhoavaButton";
import ManuaaliprosessiModal from "../components/ManuaaliprosessiModal";
import type { ManuaaliprosessiTiedot } from "../components/ManuaaliprosessiModal";
import TodennusValintaModal from "../components/TodennusValintaModal";
import {
  PAGE_MAX_WIDTH,
  RIGHT_PANEL_WIDTH,
  SIDE_NAV_WIDTH,
} from "../layout/constants";
import type { Tukipyynto } from "../types";
import { useTodellinenRooli } from "../hooks/useTodellinenRooli";
import {
  useGetKayttajaCustomQuery,
  useGetOfficersQuery, // full user by uid (base + customfields)
  useGetTukipyynnotByKayttajaQuery,
  useLockUserMutation, // lukitse käyttäjätunnus
  useUnlockUserMutation, // vapauta käyttäjätunnus
  useDeleteKayttajaMutation, //softDeleted = true
  useBypassIdProofingMutation,
} from "../api/backendApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useTranslation } from "react-i18next";
import { getDokumenttiTila } from "../utils/dokumentit";

/* Page-säiliö hostaa absoluuttisen HeroBackdropin: position:relative
   antaa anchorin, isolation:isolate luo stacking-kontekstin, jossa
   z-index:-1 backdrop pysyy kaikkien sisarsisältöjen takana. */
const Page = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

/* BackButton elää nyt Hero:n sisällä gradient-alueella, joten teksti ja
   nuoli ovat valkoisia. position:relative + z-index:1 nostavat sen
   HeroPatternin päälle. */
const BackButton = styled.button`
  position: relative;
  z-index: 1;
  background: transparent;
  border: 0;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.brandBase};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};

  & > svg {
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.brandBase};
  }
`;

/* Hero on lyhyt content-elementti — gradient ja pattern eivät enää asu
   tässä, vaan absoluuttisesti positionoidussa HeroBackdropissa, joka
   ulottuu sisältökorttien taakse pidemmälle alaspäin. Negative margins
   (-l) liimaavat sisältöalueen reunoihin (sama scope kuin backdropilla),
   side+top padding palauttavat sisällön korttien linjaan. Padding-bottom
   xxl + Page gap l = ~64px gap viimeisen badgin ja ensimmäisen kortin
   välillä. */
const Hero = styled.div`
  position: relative;
  color: ${({ theme }) => theme.colors.blackBase};
  margin: -${({ theme }) => theme.spacing.s} -${({ theme }) => theme.spacing.s}
    0;
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.s}
    ${({ theme }) => theme.spacing.xxl};
`;

/* HeroBackdrop — absoluuttinen gradient + pattern -kerros. Korkeus 450px
   ulottuu suurinpiirtein ensimmäisen kortin alalaitaan, jolloin fade
   sulautuu sen taustalla page-bg:hen ennen toista korttia. z-index:-1 +
   Page:n isolation pitää sen kaiken sisällön takana.

   Right-offset on dynaaminen calc(min(...)): kun viewport ≤ Content
   max-widthin reunaan (= SIDE_NAV + PAGE_MAX_WIDTH), backdrop ulottuu
   tasan Content-alueen reunaan (-spacing.l, kuten ennen). Kun viewport
   on tätä leveämpi, backdrop venyy lisäksi viewportin oikeaan reunaan
   asti, jolloin gradient ei enää katkea kesken kun Content max-width
   loppuu — fade jatkuu sivunavin oikealta puolelta viewportin reunaan.
   Top + left säilyvät kuten ennen (sticky to header / Content-reunaan). */
const HeroBackdrop = styled.div`
  position: absolute;
  top: -${({ theme }) => theme.spacing.s};
  left: -${({ theme }) => theme.spacing.s};
  right: calc(
    min(0px, ${SIDE_NAV_WIDTH} + ${PAGE_MAX_WIDTH} - 100vw) -
      ${({ theme }) => theme.spacing.s}
  );
  height: 450px;
  z-index: -1;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => theme.colors.highlightLight2} 0%,
    ${({ theme }) => theme.colors.highlightLight2} 45%,
    ${({ theme }) => theme.colors.depthLight3} 100%
  );

  /* Pattern overlay: smooth bell-curve mask (transparent → black 50% →
     transparent) → ikonit feidaavat hienovaraisesti kummassakin laidassa
     ilman töksähtäviä reunoja. Opacity 0.35 himmentää patternin entistä
     hienovaraisemmaksi.

     Pattern + mask ankkuroidaan Page-alueeseen rajoittamalla ::before:n
     leveys PAGE_MAX_WIDTH:iin (vakio backdropin koosta riippumatta).
     Tällöin isolla näytöllä, kun backdrop venyy viewportin reunaan,
     pattern pysyy Page:n keskellä eikä siirry tekstien alle. min(100%,...)
     varmistaa että pieneillä viewporteilla ::before ei ylitä backdropia. */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: min(100%, ${PAGE_MAX_WIDTH});
    background-image: url("/patterns/kayttajakortti-pattern.svg");
    background-repeat: no-repeat;
    background-position: center top;
    background-size: auto 288px;
    opacity: 0.35;
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0%,
      black 50%,
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      transparent 0%,
      black 50%,
      transparent 100%
    );
  }
`;

const HeroTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.m};
`;

const HeroLeft = styled.div``;

const HeroUid = styled.div`
  font-family: "Courier New", monospace;
  color: ${({ theme }) => theme.colors.blackBase};
  font-size: 0.875rem;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const HeroEmail = styled.div`
  color: ${({ theme }) => theme.colors.blackBase};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const HeroBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.s};
`;

const HeroRight = styled.div`
  text-align: right;
  flex-shrink: 0;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const HeroLabel = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const Duplikaatti = styled.div`
  position: relative;
  z-index: 1;
  margin-top: ${({ theme }) => theme.spacing.m};
  background: ${({ theme }) => theme.colors.warningBase};
  color: ${({ theme }) => theme.colors.blackBase};
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.m};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.875rem;
`;

const DuplikaattiButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  font-family: "Courier New", monospace;
  cursor: pointer;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

/* Toiminnot-toolbar: horisontaali rivi käyttäjäkortin toimintopainikkeista.
   Sijaitsee Heron jälkeen, ennen sisältökortteja. Painikkeet ovat
   kontekstuaalisia (näkyvät vain kun toiminto on relevantti). Kaikki
   toiminnot avaavat PerusteModalin briefin audit-trail-vaatimuksen
   mukaisesti (peruste pakollinen). */
const Toiminnot = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.s};
  align-items: center;
`;

const Layout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.s};
  align-items: flex-start;
`;

const Vasen = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

const Oikea = styled.div`
  width: ${RIGHT_PANEL_WIDTH};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`;

/* Wrapper KortinHyppylinkit-targetille. scroll-margin-top huomioi sticky-
   bar:n korkeuden — kun käyttäjä klikkaa hyppylinkkiä, scrollIntoView vie
   kortin yläreunan sticky-barin alalaitaan, ei sen alle piiloon. */
const JumpTarget = styled.section`
  scroll-margin-top: 4rem;
`;

const Sektio = styled.section`
  background: ${({ theme }) => theme.colors.whiteBase};
  border: 1px solid ${({ theme }) => theme.colors.depthLight1};
  padding: ${({ theme }) => theme.spacing.m};
`;

const SektioOtsikko = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid ${({ theme }) => theme.colors.brandBase};
  padding-bottom: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

const SektioTitle = styled.h2`
  margin: 0;
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.brandBase};
`;

const SektioMerkinta = styled.span`
  font-family: "Courier New", monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
`;

const TukipyyntoLista = styled.div`
  display: flex;
  flex-direction: column;
`;

const TukipyyntoButton = styled.button`
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.xs};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.s};
  width: 100%;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.highlightLight4};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

/* G2 (22.6.2026): puhelinaspan read-only rivi. Sama asettelu kuin klikattava
   TukipyyntoButton, mutta ilman hoveria, kursoria ja navigointia — aspa näkee
   tilan voidakseen kertoa asiakkaalle, muttei pääse tukipyyntöön sisään. */
const TukipyyntoStaticRow = styled.div`
  padding: ${({ theme }) => theme.spacing.s} ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.s};
  width: 100%;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const TukipyyntoBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const TukipyyntoAihe = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.blackBase};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TukipyyntoMeta = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const TukipyyntoRyhmaTitle = styled.div`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const NotFoundBox = styled.div`
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.depthDark1};
`;

interface SektioProps {
  otsikko: string;
  merkinta?: string;
  children: React.ReactNode;
}

function SektioCard({ otsikko, merkinta, children }: SektioProps) {
  // KayttajaKortti haluaa kaikki kortit kiinni oletuksena (workshop D4).
  return (
    <SektioHaitari otsikko={otsikko} merkinta={merkinta} defaultOpen={false}>
      {children}
    </SektioHaitari>
  );
}

/* Yksittäinen tukipyyntörivi tukipyyntöhistoriassa. readOnly=true (G2,
   puhelinaspa) → ei-klikattava staattinen rivi; muuten klikattava nappi joka
   avaa tukipyynnön. Sisältö identtinen molemmissa. */
function TukipyyntoRivi({
  tp,
  readOnly,
  onAvaa,
}: {
  tp: Tukipyynto;
  readOnly: boolean;
  onAvaa: () => void;
}) {
  const { t, i18n } = useTranslation(["kayttajaKortti", "common"]);
  const dateLocale = i18n.language === "en" ? "en-GB" : "fi-FI";
  const sisalto = (
    <>
      <TukipyyntoBody>
        <TukipyyntoAihe>
          {tp.tikettinumero} — {t(`common:tukipyynto.aihe.${tp.aihe}`)}
        </TukipyyntoAihe>
        <TukipyyntoMeta>
          {formatFinnishDateShort(tp.saapunut)}
          {tp.kasittelijaNimi && ` · ${tp.kasittelijaNimi}`}
        </TukipyyntoMeta>
      </TukipyyntoBody>
      <TilaBadge tila={tp.tila} />
    </>
  );
  if (readOnly) {
    return <TukipyyntoStaticRow>{sisalto}</TukipyyntoStaticRow>;
  }
  return (
    <TukipyyntoButton type="button" onClick={onAvaa}>
      {sisalto}
    </TukipyyntoButton>
  );
}

type ToimintoTyyppi =
  | "todenna"
  | "lukitse_kt" // Lukitse käyttäjätunnus (manuaalinen)
  | "vapauta_kt" // Vapauta käyttäjätunnuksen lukitus
  | "vapauta_ip" // Vapauta ID-proofing-lukitus
  | "poista"
  | "taikanappi"; // K5 3g: ohita ID-proofing virkailijan päätöksellä

export default function KayttajaKortti() {
  const { t, i18n } = useTranslation(["kayttajaKortti", "common"]);
  const [toimintoVirhe, setToimintoVirhe] = useState<string | null>(null);
  const [unlockUser] = useUnlockUserMutation();
  const [lockUser] = useLockUserMutation();
  const [deleteKayttaja] = useDeleteKayttajaMutation(); // ← Add this
  const muistiinpanotToteutettu = false;
  const { uid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppState();
  const theme = useTheme();

  const [avoinToiminto, setAvoinToiminto] = useState<ToimintoTyyppi | null>(
    null,
  );
  const [manuaaliprosessiAuki, setManuaaliprosessiAuki] = useState(false);
  const [todennusValintaAuki, setTodennusValintaAuki] = useState(false);
  const { todellinenRooli, isLoading, error } = useTodellinenRooli();
  const { data: kayttaja, isLoading: kayttajaLataa } =
    useGetKayttajaCustomQuery(uid ?? skipToken);
  const [bypassIdProofing, { isLoading: bypassLoading }] =
    useBypassIdProofingMutation(); // ← ADD THIS
  const { data: tukipyynnot = [] } = useGetTukipyynnotByKayttajaQuery(
    uid ?? skipToken,
  );
  /* B3 vieritysbugin korjaus (22.6.2026): suomifi Modal lukitsee body-scrollin
     (overflow:hidden) auetessaan ja palauttaa sen vasta onAfterClose-
     callbackissa. PerusteModal renderöidään ehdollisesti, joten kun
     avoinToiminto nollataan vahvistuksessa, modaali unmountataan auki-tilassa
     eikä onAfterClose laukea → body jää lukkoon ja vieritys ei toimi enää edes
     näkymää vaihdettaessa (esim. takaisin tukipyynnölle). Varmistus: kun mikään
     modaali ei ole auki, palautetaan body-scroll käyttöön. */

  useEffect(() => {
    const jokuModaaliAuki = todennusValintaAuki || manuaaliprosessiAuki;

    if (jokuModaaliAuki) {
      document.body.style.overflow = "hidden";
      document.body.setAttribute("data-fi-modal", "true");
    } else {
      // Use requestAnimationFrame to let state settle
      requestAnimationFrame(() => {
        document.body.style.overflow = "auto";
        document.body.removeAttribute("data-fi-modal");
      });
    }
  }, [todennusValintaAuki, manuaaliprosessiAuki]);
  /* Navigaatio-ketjun säilyttäminen: jos tultiin tukipyyntönäkymästä, sieltä
     kuljettiin myös listSearch + listScrollY (tukipyyntölistan filtterit
     ja scroll-position). Näitä tarvitaan jotta paluu listalle Back-painikkeen
     kautta vie täysin samaan tilaan kuin alkuperäinen lista. */
  const navigaatioTila = location.state as {
    fromTukipyyntoId?: string;
    listSearch?: string;
    listScrollY?: number;
    // Käyttäjähausta saavuttaessa: haun query + scroll, jotta "Takaisin
    // hakutuloksiin" palauttaa täsmälleen saman hakutilan (MR5).
    fromKayttajahaku?: boolean;
    kayttajahakuSearch?: string;
    kayttajahakuScrollY?: number;
  } | null;
  const fromTukipyyntoId = navigaatioTila?.fromTukipyyntoId;
  const listSearch = navigaatioTila?.listSearch;
  const listScrollY = navigaatioTila?.listScrollY;
  const fromKayttajahaku = navigaatioTila?.fromKayttajahaku;
  const kayttajahakuSearch = navigaatioTila?.kayttajahakuSearch;
  const kayttajahakuScrollY = navigaatioTila?.kayttajahakuScrollY;

  /* Kontekstitietoinen Takaisin (MR5): tukipyyntöön → käyttäjähaun
     hakutuloksiin → muuten selaimen historiaan. Käyttäjähakuun palatessa
     kuljetetaan palauttavaRivi (uid) + listScrollY, jotta haku palautuu
     samaan tilaan ja oikea rivi vieritetään näkyviin. */
  function palaaTakaisin() {
    if (fromTukipyyntoId) {
      navigate(`/tukipyynnot/${fromTukipyyntoId}`, {
        state: { listSearch, listScrollY },
      });
    } else if (fromKayttajahaku) {
      navigate(`/kayttajahaku${kayttajahakuSearch ?? ""}`, {
        state: { palauttavaRivi: uid, listScrollY: kayttajahakuScrollY },
      });
    } else {
      navigate(-1);
    }
  }

  /* Hyppylinkit-bar:n scroll-trigger: kun BackButton poistuu viewportista
     (käyttäjä on vierittänyt heron ja back-painikkeen ohi), sticky-bar
     materialisoituu. Ei erillistä sentinel-elementtiä — ei lisää flex-
     childejä Page-säiliöön (gap olisi muuten lisännyt ~16px shiftin). */
  const backButtonRef = useRef<HTMLButtonElement>(null);

  // G2: puhelinaspa näkee tukipyyntölistan tilat, muttei pääse sisään →
  // read-only kun rooli ei saa avata yksittäistä tukipyyntöä.
  const tukipyynnotReadOnly = !voiNahda(
    "tukipyynnot.avaa_yksittainen",
    todellinenRooli?.profiili,
  );

  /* B1 (22.6.2026): henkilöllisyyden todentamisen reitit. "Todenna
     henkilöllisyys" -painike avaa valintamodaalin, joka tarjoaa nämä
     vaihtoehdot. Taikanappi ei ole enää oma punainen painike. */
  const voiTodentaaManuaalisesti = voiNahda(
    "kayttajat.todenna_manuaalisesti",
    todellinenRooli?.profiili,
  );

  const voiOhittaaProofing =
    voiNahda("kayttajat.taikanappi", todellinenRooli?.profiili) &&
    (!kayttaja?.idProofingYritykset ||
      (kayttaja?.idProofingYritykset > 0 &&
        kayttaja.dokumentit.some(
          (d) => getDokumenttiTila(d.vanhenee) === "voimassa",
        )));

  const naytaTodennusValinta =
    !kayttaja?.henkilollisyysTodennettu &&
    (voiTodentaaManuaalisesti || voiOhittaaProofing);

  const { data: duplikaattiKayttaja } = useGetKayttajaCustomQuery(
    kayttaja?.duplikaattiUid ?? skipToken,
  );

  const { data: officers, isLoading: officersLataa } = useGetOfficersQuery();
  const officer = officers?.find((o) => o.id === uid);

  async function vahvistaToiminto(peruste: string) {
    if (!avoinToiminto || !kayttaja) return;
    const aika = new Date().toISOString();
    const tekija = officer?.name.givenName + " " + officer?.name.familyName;
    const tekijaUid = officer?.id;
    const kayttajaNimi = kayttaja?.etunimi + " " + kayttaja?.sukunimi;
    if (avoinToiminto === "todenna") {
      dispatch({
        type: "TODENNA_HENKILOLLISYYS",
        kayttajaUid: kayttaja.uid,
        entry: {
          id: `aud-${Date.now()}`,
          tyyppi: "henkilollisyys_todennettu",
          kuvaus:
            "Henkilöllisyys todennettu manuaalisesti virkailijan toimesta",
          tekija,
          tekijaUid: tekijaUid ?? "unknown",
          aika,
          peruste,
        },
      });
    } else if (avoinToiminto === "lukitse_kt") {
      try {
        await lockUser({ uid: kayttaja.uid, lockReason: peruste }).unwrap();
      } catch (e) {
        setToimintoVirhe(t("virhe.lukitus_epaonnistui"));
        console.error("Lock failed:", e);
        return;
      }
    } else if (avoinToiminto === "vapauta_kt") {
      try {
        await unlockUser({ uid: kayttaja.uid, logEventText: peruste }).unwrap();
      } catch (e) {
        setToimintoVirhe(t("virhe.lukituksen_avaus_epaonnistui"));
        console.error("Unlock failed:", e);
        return;
      }
    } else if (avoinToiminto === "vapauta_ip") {
      dispatch({
        type: "VAPAUTA_LUKITUS",
        kayttajaUid: kayttaja.uid,
        entry: {
          id: `aud-${Date.now()}`,
          tyyppi: "lukitus_vapautettu",
          kuvaus: "ID-proofing-lukitus vapautettu virkailijan toimesta",
          tekija,
          tekijaUid: tekijaUid ?? "unknown",
          aika,
          peruste,
        },
      });
    } else if (avoinToiminto === "poista") {
      const hasOpenTickets = tukipyynnot.some(
        (tp) => tp.tila === "avoin" || tp.tila === "käsittelyssä" || !tp.tila,
      );

      if (hasOpenTickets) {
        setToimintoVirhe(t("virhe.poista_ei_sallittu_avoimet_tiketti"));
        setAvoinToiminto(null);
        return;
      }

      try {
        await deleteKayttaja({
          uid: kayttaja.uid,
        }).unwrap();

        // Success - navigate back
        palaaTakaisin();
      } catch (e) {
        setToimintoVirhe(t("virhe.poisto_epaonnistui"));
        console.error("Delete failed:", e);
        return;
      }
    } else if (avoinToiminto === "taikanappi") {
      /* K5 3g: Ohita ID-proofing virkailijan päätöksellä */
      try {
        await bypassIdProofing({
          uid: kayttaja.uid,
          perustekentta: peruste,
        }).unwrap();
      } catch (error: any) {
        console.error("Bypass failed:", error);

        // ✅ Extract error message - handle 409 duplicate and other errors
        const statusCode = error?.status || 500;
        const errorMessage =
          error?.data?.message ||
          error?.data?.error ||
          error?.message ||
          t("virhe.tuntematon_virhe");

        if (statusCode === 409) {
          // Duplicate document error
          setToimintoVirhe(
            `${t("virhe.duplikaatti_asiakirja")}: ${errorMessage}`,
          );
        } else if (statusCode === 403) {
          // Permission error
          setToimintoVirhe(errorMessage);
        } else {
          setToimintoVirhe(t("virhe.bypass_epaonnistui"));
        }
        setAvoinToiminto(null);
        return;
      }
    }

    setAvoinToiminto(null);
  }

  if (isLoading || kayttajaLataa) return <>Ladataan...</>;
  if (!voiNahda("kayttajat.avaa_kortti", todellinenRooli?.profiili)) {
    return <Navigate to="/" replace />;
  }
  if (!kayttaja) {
    return <NotFoundBox>{t("ei.kayttajaa", { uid })}</NotFoundBox>;
  }

  const toimintoModalit: Record<
    ToimintoTyyppi,
    { otsikko: string; kuvaus: string }
  > = {
    todenna: {
      otsikko: t("modal.todenna.otsikko"),
      kuvaus: t("modal.todenna.kuvaus", { nimi: naytaNimi(kayttaja) }),
    },
    lukitse_kt: {
      otsikko: t("modal.lukitse.otsikko"),
      kuvaus: t("modal.lukitse.kuvaus", { nimi: naytaNimi(kayttaja) }),
    },
    vapauta_kt: {
      otsikko: t("modal.vapauta.otsikko"),
      kuvaus: t("modal.vapauta.kuvaus", { nimi: naytaNimi(kayttaja) }),
    },
    vapauta_ip: {
      otsikko: t("modal.vapauta_idproofing.otsikko"),
      kuvaus: t("modal.vapauta_idproofing.kuvaus", {
        nimi: naytaNimi(kayttaja),
      }),
    },
    poista: {
      otsikko: t("modal.poista.otsikko"),
      kuvaus: t("modal.poista.kuvaus", { nimi: naytaNimi(kayttaja) }),
    },
    taikanappi: {
      otsikko: t("modal.taikanappi.otsikko"),
      kuvaus: t("modal.taikanappi.kuvaus", { nimi: naytaNimi(kayttaja) }),
    },
  };

  return (
    <Page>
      <HeroBackdrop aria-hidden />
      <BackButton ref={backButtonRef} onClick={palaaTakaisin} type="button">
        <IconArrowLeft aria-hidden />
        {fromTukipyyntoId
          ? t("takaisin.tukipyyntoon")
          : fromKayttajahaku
          ? t("takaisin.hakutuloksiin")
          : t("takaisin.takaisin")}
      </BackButton>

      {toimintoVirhe && (
        <InlineAlert
          status="error"
          labelText={t("virhe.otsikko")}
          onClick={() => setToimintoVirhe(null)} // dismiss on close
        >
          {toimintoVirhe}
        </InlineAlert>
      )}

      <KortinHyppylinkit
        sentinelRef={backButtonRef}
        kohdat={(
          [
            { id: "sektio-asiakirjat", label: "Asiakirjat" },
            kayttaja.idProofingYritykset > 0 &&
            voiNahda(
              "kayttajat.tapahtumat_yritykset",
              todellinenRooli?.profiili,
            )
              ? { id: "sektio-henkilollisyys", label: "Henkilöllisyys" }
              : null,
            { id: "sektio-laitteet", label: "Laitteet" },
            voiNahda(
              "kayttajat.tapahtumat_yritykset",
              todellinenRooli?.profiili,
            )
              ? { id: "sektio-tapahtumat", label: "Tapahtumat" }
              : null,
            voiNahda("kayttajat.nae_tukipyyntolista", todellinenRooli?.profiili)
              ? { id: "sektio-tukipyynnot", label: "Tukipyynnöt" }
              : null,
            voiNahda("kayttajat.audit_trail", todellinenRooli?.profiili)
              ? { id: "sektio-toimenpiteet", label: "Toimenpiteet" }
              : null,
          ] satisfies (HyppylinkkiKohde | null)[]
        ).filter((k): k is HyppylinkkiKohde => k !== null)}
      />

      <Layout>
        <Vasen>
          {kayttaja.dokumentit && (
            <JumpTarget id="sektio-asiakirjat">
              <SektioCard
                otsikko={t("otsikko.asiakirjat")}
                merkinta={`${
                  kayttaja.dokumentit.length === 0
                    ? kayttaja.identityDocument
                      ? 1
                      : kayttaja.dokumentit.length
                    : kayttaja.dokumentit.length
                } kpl`}
              >
                <Dokumentit
                  dokumentit={kayttaja.dokumentit}
                  uid={kayttaja.uid}
                  identityDocument={kayttaja.identityDocument}
                />
              </SektioCard>
            </JumpTarget>
          )}

          {kayttaja.idProofingYritykset > 0 &&
            voiNahda(
              "kayttajat.tapahtumat_yritykset",
              todellinenRooli?.profiili,
            ) && (
              <JumpTarget id="sektio-henkilollisyys">
                <SektioCard
                  otsikko={t("otsikko.henkilollisyys")}
                  merkinta={`${kayttaja.idProofingYritykset}`}
                >
                  <HenkilollisyydenTodentaminen kayttaja={kayttaja} />
                </SektioCard>
              </JumpTarget>
            )}
          {kayttaja.laitteet && (
            <JumpTarget id="sektio-laitteet">
              <SektioCard
                otsikko={t("otsikko.laitteet")}
                merkinta={`${kayttaja.laitteet.length} kpl`}
              >
                <Laitteet laitteet={kayttaja.laitteet} />
              </SektioCard>
            </JumpTarget>
          )}
          {voiNahda(
            "kayttajat.tapahtumat_yritykset",
            todellinenRooli?.profiili,
          ) &&
            kayttaja.tapahtumat && (
              <JumpTarget id="sektio-tapahtumat">
                <SektioCard
                  otsikko={t("otsikko.tapahtumat")}
                  merkinta={`${kayttaja.tapahtumat.length} kpl`}
                >
                  <Tapahtumat
                    tapahtumat={kayttaja.tapahtumat}
                    laitteet={kayttaja.laitteet}
                  />
                </SektioCard>
              </JumpTarget>
            )}
          {voiNahda(
            "kayttajat.nae_tukipyyntolista",
            todellinenRooli?.profiili,
          ) && (
            <JumpTarget id="sektio-tukipyynnot">
              <SektioCard
                otsikko={t("otsikko.historia")}
                merkinta={`${tukipyynnot.length} kpl`}
              >
                {tukipyynnot.length === 0 ? (
                  <Empty>{t("ei.tukipyyntoja")}</Empty>
                ) : (
                  <>
                    {(() => {
                      const avoimet = tukipyynnot.filter(
                        (tp) =>
                          tp.tila === "avoin" || tp.tila === "käsittelyssä",
                      );
                      const ratkaistut = tukipyynnot.filter(
                        (tp) =>
                          tp.tila === "ratkaistu" ||
                          tp.tila === "self-resolved",
                      );
                      return (
                        <>
                          <TukipyyntoRyhmaTitle>
                            Avoimet ({avoimet.length})
                          </TukipyyntoRyhmaTitle>
                          {avoimet.length === 0 ? (
                            <Empty>{t("ei.avoimia_tukipyyntoja")}</Empty>
                          ) : (
                            <TukipyyntoLista>
                              {avoimet.map((tp) => (
                                <TukipyyntoRivi
                                  key={tp.tikettinumero}
                                  tp={tp}
                                  readOnly={tukipyynnotReadOnly}
                                  onAvaa={() =>
                                    navigate(
                                      `/tukipyynnot/${tp.tikettinumero.replace(
                                        "#",
                                        "",
                                      )}`,
                                      {
                                        state: { fromKayttajaUid: uid },
                                      },
                                    )
                                  }
                                />
                              ))}
                            </TukipyyntoLista>
                          )}

                          <TukipyyntoRyhmaTitle style={{ marginTop: "1rem" }}>
                            Ratkaistut ({ratkaistut.length})
                          </TukipyyntoRyhmaTitle>
                          {ratkaistut.length === 0 ? (
                            <Empty>Ei ratkaistuja tukipyyntöjä.</Empty>
                          ) : (
                            <TukipyyntoLista>
                              {ratkaistut.map((tp) => (
                                <TukipyyntoRivi
                                  key={tp.tikettinumero}
                                  tp={tp}
                                  readOnly={tukipyynnotReadOnly}
                                  onAvaa={() =>
                                    navigate(
                                      `/tukipyynnot/${tp.tikettinumero.replace(
                                        "#",
                                        "",
                                      )}`,
                                      {
                                        state: { fromKayttajaUid: uid },
                                      },
                                    )
                                  }
                                />
                              ))}
                            </TukipyyntoLista>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </SektioCard>
            </JumpTarget>
          )}
          {voiNahda("kayttajat.audit_trail", todellinenRooli?.profiili) &&
            kayttaja.auditTrail && (
              <JumpTarget id="sektio-toimenpiteet">
                <SektioCard
                  otsikko={t("otsikko.toimenpiteet")}
                  merkinta={`${kayttaja.auditTrail.length} merkintää`}
                >
                  <AuditTrail entries={kayttaja.auditTrail} />
                </SektioCard>
              </JumpTarget>
            )}
        </Vasen>

        <Oikea>
          <KayttajaSidebar
            kayttaja={kayttaja}
            naytaJonossaTagi
            toiminnotSlot={
              /* Poistettu (soft delete) -käyttäjä: kortti read-only — piilota
                 kaikki tiliä muokkaavat toiminnot (lukitse/todenna/vapauta/poista).
                 Virkailija tarvitsee tiedot vain katseltavaksi. */
              kayttaja.poistettu ? undefined : (
                <>
                  {naytaTodennusValinta && (
                    <Button
                      variant="default"
                      icon={<IconCheckCircle />}
                      onClick={() => setTodennusValintaAuki(true)}
                    >
                      {t("modal.todenna.otsikko")}
                    </Button>
                  )}
                  {kayttaja.lukittu &&
                    voiNahda(
                      "kayttajat.vapauta_ip_lukitus",
                      todellinenRooli?.profiili,
                    ) && (
                      <Button
                        variant="default"
                        icon={<IconLock />}
                        onClick={() => setAvoinToiminto("vapauta_ip")}
                      >
                        {t("modal.vapauta_idproofing.otsikko")}
                      </Button>
                    )}
                  {!kayttaja.kayttajatunnusLukittu &&
                    voiNahda(
                      "kayttajat.lukitse_tunnus",
                      todellinenRooli?.profiili,
                    ) && (
                      <Button
                        variant="secondary"
                        icon={<IconLock />}
                        onClick={() => setAvoinToiminto("lukitse_kt")}
                      >
                        {t("nappi.lukitse")}
                      </Button>
                    )}
                  {kayttaja.kayttajatunnusLukittu &&
                    voiNahda(
                      "kayttajat.vapauta_tunnus_lukitus",
                      todellinenRooli?.profiili,
                    ) && (
                      <Button
                        variant="default"
                        icon={<IconLock />}
                        onClick={() => setAvoinToiminto("vapauta_kt")}
                      >
                        {t("nappi.vapauta")}
                      </Button>
                    )}
                  {!kayttaja.poistettu &&
                    voiNahda("kayttajat.poista", todellinenRooli?.profiili) && (
                      <TuhoavaButton
                        icon={<IconRemove />}
                        onClick={() => setAvoinToiminto("poista")}
                      >
                        {t("nappi.poista")}
                      </TuhoavaButton>
                    )}
                  {/* B1 (22.6.2026): erillinen punainen taikanappi poistettu.
                    ID-proofingin ohitus on nyt "Todenna henkilöllisyys"
                    -painikkeen takana avautuvassa valintamodaalissa, jotta se
                    ei ole heti tyrkyllä. */}
                </>
              )
            }
          />
          {/*{voiNahda("kayttajat.lue_muistiinpanot", todellinenRooli?.profiili) &&
            muistiinpanotToteutettu && (
              <MuistiinpanoSticky kayttajaUid={kayttaja.uid} />
            )}*/}
        </Oikea>
      </Layout>

      <TodennusValintaModal
        visible={todennusValintaAuki}
        kayttajaNimi={naytaNimi(kayttaja)}
        naytaManuaaliprosessi={voiTodentaaManuaalisesti}
        naytaTaikanappi={voiOhittaaProofing}
        onValitseManuaali={() => {
          setTodennusValintaAuki(false);
          setManuaaliprosessiAuki(true);
        }}
        onValitseTaikanappi={() => {
          setTodennusValintaAuki(false);
          setAvoinToiminto("taikanappi");
        }}
        onCancel={() => setTodennusValintaAuki(false)}
      />

      {avoinToiminto && (
        <PerusteModal
          otsikko={toimintoModalit[avoinToiminto].otsikko}
          kuvaus={toimintoModalit[avoinToiminto].kuvaus}
          visible={avoinToiminto !== null}
          onConfirm={vahvistaToiminto}
          onCancel={() => setAvoinToiminto(null)}
          perusteVapaaehtoinen={
            avoinToiminto === "lukitse_kt" || avoinToiminto === "poista"
          }
          tuhoava={avoinToiminto === "poista"}
          isLoading={avoinToiminto === "taikanappi" ? bypassLoading : false} // ← ADD THIS
        />
      )}

      <ManuaaliprosessiModal
        visible={manuaaliprosessiAuki}
        kayttajaSahkoposti={kayttaja.sahkoposti}
        kayttajaId={kayttaja.uid}
        onConfirm={() => {
          setManuaaliprosessiAuki(false);
        }}
        onCancel={() => setManuaaliprosessiAuki(false)}
      />
    </Page>
  );
}
